// RefurbishmentDetail.jsx - Updated UI with two sections
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import config from '../../config';
import Progressbar from './Progressbar';

const API_SERVER = config.API_BASE_URL;

function RefurbishmentDetail() {
  const navigate = useNavigate();
  const params = useParams();

  const encryptedRepoId = params.repoId || params.id;
  const repoId = encryptedRepoId ? atob(encryptedRepoId) : '';

  const [repoCurrentStep, setRepoCurrentStep] = useState('Pending');

  const userData = useSelector((state) => state.userInfo);
  const role = (userData?.roleIdWithName?.[0]?.role_name || '').toLowerCase();

  // ==========================================
  // STATES
  // ==========================================

  const [loading, setLoading] = useState(false);
  const [postLoading, setPostLoading] = useState(false);
  const [repoData, setRepoData] = useState(null);

  const [formData, setFormData] = useState({
    estimated_cost: '',
    vendor_name: '',
    refurbishment_notes: '',
    rto_charges: 'No',
    rto_charges_by: '',
    challan_status: 'No',
    challan_number: '',
    challan_date: '',
    challan_type: '',
    challan_amount: '',
    insurance_validity: 'Yes',
    fitness_validity: 'Yes',
    other_expenses: 'No',
    other_expenses_amount: '',
    additional_information: '',
  });

  // ==========================================
  // FETCH ON MOUNT
  // ==========================================

  useEffect(() => {
    fetchRepoDetails();
    fetchRepoProgress();
    fetchRefurbishmentData();
  }, []);

  const fetchRepoDetails = async () => {
    try {
      const response = await axios.get(`${API_SERVER}getVehicleRepoById/${repoId}`);
      if (response.data.status) {
        setRepoData(response.data.data);
      }
    } catch (error) {
      console.log(error);
      toast.error('Failed to load repo details');
    }
  };

  const fetchRepoProgress = async () => {
    try {
      const res = await fetch(`${API_SERVER}get-repo-progress-flow?repo_id=${repoId}`);
      const result = await res.json();
      if (result.status) {
        setRepoCurrentStep(result.current_step);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchRefurbishmentData = async () => {
    try {
      const response = await axios.get(`${API_SERVER}get-refurbishment-details/${repoId}`);

      console.log(response.data);

      if (response.data.status && response.data.data) {
        const d = response.data.data;

        setFormData({
          estimated_cost: d.estimated_cost || '',

          vendor_name: d.vendor_name || '',

          refurbishment_notes: d.refurbishment_notes || '',

          rto_charges: d.rto_charges || d.checklist_rto_charges || 'No',

          rto_charges_by: d.rto_charges_by || d.checklist_rto_charges_by || '',

          challan_status: d.challan_status || d.checklist_challan_status || 'No',

          challan_number: d.challan_number || d.checklist_challan_number || '',

          challan_date: d.challan_date || d.checklist_challan_date || '',

          challan_type: d.challan_type || d.checklist_challan_type || '',

          challan_amount: d.challan_amount || d.checklist_challan_amount || '',

          insurance_validity: d.insurance_validity || d.checklist_insurance_validity || 'Yes',

          fitness_validity: d.fitness_validity || d.checklist_fitness_validity || 'Yes',

          other_expenses: d.other_expenses || d.checklist_other_expenses || 'No',

          other_expenses_amount: d.other_expenses_amount || d.checklist_other_expenses_amount || '',

          additional_information: d.additional_information || d.checklist_additional_information || '',
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // ==========================================
  // SUBMIT TO REGIONAL HEAD
  // ==========================================

  const handleSubmitToRH = async () => {
    if (!formData.estimated_cost || !formData.vendor_name || !formData.refurbishment_notes) {
      toast.error('Please fill all mandatory fields');
      return;
    }

    try {
      setPostLoading(true);
      setLoading(true);

      const payload = {
        repo_id: repoId,
        estimated_cost: formData.estimated_cost,
        vendor_name: formData.vendor_name,
        refurbishment_notes: formData.refurbishment_notes,
        challan_number: formData.challan_number,
        challan_date: formData.challan_date,
        challan_type: formData.challan_type,
        rto_charges: formData.rto_charges,
        rto_charges_by: formData.rto_charges_by,
        challan_status: formData.challan_status,
        challan_amount: formData.challan_amount,
        insurance_validity: formData.insurance_validity,
        fitness_validity: formData.fitness_validity,
        other_expenses: formData.other_expenses,
        other_expenses_amount: formData.other_expenses_amount,
        additional_information: formData.additional_information,
        created_by: userData?.id,
      };

      const response = await axios.post(`${API_SERVER}submitRefurbishment`, payload);

      if (response.data.status) {
        toast.success('Sent To Regional Head');
        navigate('/dashboard/view-repo-list');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
      setPostLoading(false);
    }
  };

  // ==========================================
  // FORWARD TO CREDIT HEAD
  // ==========================================

  const handleForwardToCH = async () => {
    setPostLoading(true);
    try {
      const response = await axios.post(`${API_SERVER}forwardRefurbishmentToCH`, {
        repo_id: repoId,
        updated_by: userData?.id,
      });
      if (response.data.status) {
        toast.success('Forwarded To Credit Head');
        navigate('/dashboard/view-repo-list');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong');
    } finally {
      setPostLoading(false);
    }
  };

  // ==========================================
  // APPROVE
  // ==========================================

  const handleApprove = async () => {
    setPostLoading(true);
    try {
      const response = await axios.post(`${API_SERVER}approveRefurbishment`, {
        repo_id: repoId,
        updated_by: userData?.id,
      });
      if (response.data.status) {
        toast.success('Refurbishment Approved');
        navigate(`/dashboard/final-submission/${btoa(repoId)}`);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong');
    } finally {
      setPostLoading(false);
    }
  };

  // ==========================================
  // REJECT
  // ==========================================

  const handleReject = async () => {
    setPostLoading(true);
    try {
      const response = await axios.post(`${API_SERVER}rejectRefurbishment`, {
        repo_id: repoId,
        updated_by: userData?.id,
      });
      if (response.data.status) {
        toast.success('Refurbishment Rejected');
        navigate('/dashboard/view-repo-list');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong');
    } finally {
      setPostLoading(false);
    }
  };

  // ==========================================
  // RENDER
  // ==========================================

  return (
    <div className="container-fluid py-3">
      {/* ============================================================ */}
      {/* PRELOADER OVERLAY */}
      {/* ============================================================ */}

      {postLoading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(255,255,255,0.85)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(3px)',
          }}
        >
          <div
            style={{
              width: '56px',
              height: '56px',
              border: '5px solid #e0e0e0',
              borderTop: '5px solid #1976d2',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }}
          />
          <p style={{ marginTop: '18px', fontWeight: 600, color: '#1976d2', fontSize: '15px' }}>
            Processing, please wait...
          </p>
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                width: '320px',
                height: '14px',
                borderRadius: '6px',
                marginTop: '10px',
                background: 'linear-gradient(90deg,#e0e0e0 25%,#f5f5f5 50%,#e0e0e0 75%)',
                backgroundSize: '400% 100%',
                animation: 'shimmer 1.4s infinite',
              }}
            />
          ))}
          <style>{`
                        @keyframes spin { to { transform: rotate(360deg); } }
                        @keyframes shimmer { 0%{background-position:100% 0} 100%{background-position:-100% 0} }
                    `}</style>
        </div>
      )}

      <div className="card border-0 shadow-sm rounded-4 mb-4">
        <div className="card-body p-4">
          {/* Card Header */}
          <div className="mb-3">
            <h3 className="fw-bold mb-1">Repair Details</h3>
            <p className="text-muted mb-0">Vehicle Repair approval workflow</p>
          </div>

          {/* Progress Bar */}
          {/* <div
                        style={{
                            width: "100%",
                            overflowX: "auto",
                            overflowY: "hidden",
                            paddingBottom: "8px",
                            marginBottom: "24px"
                        }}
                    >
                        <div style={{ minWidth: "1450px" }}> */}
          {/* <Progressbar currentStep={repoCurrentStep} /> */}
          {/* </div>
                    </div> */}

          {/* Vehicle Info Row (disabled / read-only) */}
          {repoData && (
            <div className="row mb-4">
              <div className="col-md-3 mb-3">
                <label className="form-label fw-semibold">Agreement No</label>
                <input type="text" className="form-control bg-light" value={repoData?.agreement_no || ''} disabled />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label fw-semibold">Customer Name</label>
                <input type="text" className="form-control bg-light" value={repoData?.customer_name || ''} disabled />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label fw-semibold">Registration No</label>
                <input
                  type="text"
                  className="form-control bg-light"
                  value={repoData?.registration_number || ''}
                  disabled
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label fw-semibold">Vehicle Model</label>
                <input type="text" className="form-control bg-light" value={repoData?.model || ''} disabled />
              </div>
            </div>
          )}

          {/* Mandatory Repair Fields */}
          <div className="row">
            <div className="col-md-4 mb-4">
              <label className="form-label fw-semibold">
                Estimated Cost <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                className="form-control"
                name="estimated_cost"
                value={formData.estimated_cost}
                onChange={handleChange}
                placeholder="Enter estimated cost"
              />
            </div>

            <div className="col-md-4 mb-4">
              <label className="form-label fw-semibold">
                Vendor Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                name="vendor_name"
                value={formData.vendor_name}
                onChange={handleChange}
                placeholder="Enter vendor name"
              />
            </div>

            <div className="col-md-12 mb-2">
              <label className="form-label fw-semibold">
                Refurbishment Notes <span className="text-danger">*</span>
              </label>
              <textarea
                rows="5"
                className="form-control"
                name="refurbishment_notes"
                value={formData.refurbishment_notes}
                onChange={handleChange}
                placeholder="Enter refurbishment details..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* ============================================================ */}
      {/* SECTION 2 : CHALLAN DETAILS CARD (OPTIONAL)                  */}
      {/* ============================================================ */}

      <div className="card border-0 shadow-sm rounded-4 mb-4" style={{ borderLeft: '4px solid #e0e0e0' }}>
        <div className="card-body p-4">
          {/* Card Header */}
          <div className="mb-4 d-flex align-items-center gap-2">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <h5 className="fw-bold mb-0">Challan Details</h5>
                <span
                  className="badge rounded-pill"
                  style={{
                    background: '#f0f4ff',
                    color: '#5b7fd4',
                    fontSize: '11px',
                    fontWeight: 600,
                    padding: '4px 10px',
                    border: '1px solid #c8d8f8',
                  }}
                >
                  Optional
                </span>
              </div>
              <p className="text-muted mb-0" style={{ fontSize: '13px' }}>
                Challan, RTO &amp; additional expense information — not required to submit.
              </p>
            </div>
          </div>

          <div className="row">
            {/* Challan Available */}
            <div className="col-md-4 mb-3">
              <label className="form-label fw-semibold">Challan Available</label>
              <select
                className="form-control"
                name="challan_status"
                value={formData.challan_status}
                onChange={handleChange}
              >
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </div>

            {/* Conditional Challan Fields */}
            {formData.challan_status === 'Yes' && (
              <>
                <div className="col-md-4 mb-3">
                  <label className="form-label fw-semibold">Challan Number</label>
                  <input
                    className="form-control"
                    name="challan_number"
                    value={formData.challan_number}
                    onChange={handleChange}
                    placeholder="Enter challan number"
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label fw-semibold">Challan Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="challan_date"
                    value={formData.challan_date}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label fw-semibold">Challan Type</label>
                  <select
                    className="form-control"
                    name="challan_type"
                    value={formData.challan_type}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    <option value="Traffic">Traffic</option>
                    <option value="Permit">Permit</option>
                    <option value="Fitness">Fitness</option>
                    <option value="Tax">Tax</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label fw-semibold">Challan Amount</label>
                  <input
                    type="number"
                    className="form-control"
                    name="challan_amount"
                    value={formData.challan_amount}
                    onChange={handleChange}
                    placeholder="Enter challan amount"
                  />
                </div>
              </>
            )}

            {/* RTO Charges */}
            <div className="col-md-4 mb-3">
              <label className="form-label fw-semibold">RTO Charges</label>
              <select className="form-control" name="rto_charges" value={formData.rto_charges} onChange={handleChange}>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </div>

            {formData.rto_charges === 'Yes' && (
              <div className="col-md-4 mb-3">
                <label className="form-label fw-semibold">RTO Charges Borne By</label>
                <select
                  className="form-control"
                  name="rto_charges_by"
                  value={formData.rto_charges_by}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  <option value="Customer">Customer</option>
                  <option value="Ckers">Ckers</option>
                  <option value="Vendor">Vendor</option>
                </select>
              </div>
            )}

            {/* Insurance Validity */}
            <div className="col-md-4 mb-3">
              <label className="form-label fw-semibold">Insurance Validity</label>
              <select
                className="form-control"
                name="insurance_validity"
                value={formData.insurance_validity}
                onChange={handleChange}
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            {/* Fitness Validity */}
            <div className="col-md-4 mb-3">
              <label className="form-label fw-semibold">Fitness Validity</label>
              <select
                className="form-control"
                name="fitness_validity"
                value={formData.fitness_validity}
                onChange={handleChange}
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            {/* Other Expenses */}
            <div className="col-md-4 mb-3">
              <label className="form-label fw-semibold">Other Expenses</label>
              <select
                className="form-control"
                name="other_expenses"
                value={formData.other_expenses}
                onChange={handleChange}
              >
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </div>

            {formData.other_expenses === 'Yes' && (
              <div className="col-md-4 mb-3">
                <label className="form-label fw-semibold">Other Expenses Amount</label>
                <input
                  type="number"
                  className="form-control"
                  name="other_expenses_amount"
                  value={formData.other_expenses_amount}
                  onChange={handleChange}
                  placeholder="Enter amount"
                />
              </div>
            )}

            {/* Additional Information */}
            <div className="col-md-12 mb-2">
              <label className="form-label fw-semibold">Additional Information</label>
              <textarea
                rows="3"
                className="form-control"
                name="additional_information"
                value={formData.additional_information}
                onChange={handleChange}
                placeholder="Any additional notes or remarks..."
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-4">
          <div className="d-flex justify-content-end gap-2">
            <button className="btn btn-secondary px-4" onClick={() => navigate(-1)}>
              Back
            </button>

            {/* MANAGER */}
            {role === 'manager' && (
              <button className="btn btn-primary px-4" onClick={handleSubmitToRH} disabled={loading || postLoading}>
                {loading ? 'Submitting...' : 'Submit'}
              </button>
            )}

            {/* REGIONAL HEAD */}
            {role === 'regional head' && (
              <>
                <button
                  className="btn btn-danger px-4"
                  onClick={handleReject}
                  disabled={postLoading}
                  style={{ opacity: postLoading ? 0.7 : 1 }}
                >
                  Reject
                </button>

                <button
                  className="btn btn-warning px-4"
                  onClick={handleForwardToCH}
                  disabled={postLoading}
                  style={{ opacity: postLoading ? 0.7 : 1 }}
                >
                  Forward
                </button>
              </>
            )}

            {/* CREDIT HEAD */}
            {role === 'credit head' && (
              <>
                <button
                  className="btn btn-danger px-4"
                  onClick={handleReject}
                  disabled={postLoading}
                  style={{ opacity: postLoading ? 0.7 : 1 }}
                >
                  Reject
                </button>

                <button
                  className="btn btn-success px-4"
                  onClick={handleApprove}
                  disabled={postLoading}
                  style={{ opacity: postLoading ? 0.7 : 1 }}
                >
                  Approve
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RefurbishmentDetail;
